/** Travel-style values stored separately on templates — exclude from topic/collection tags. */
const TRAVEL_STYLE_TAG_KEYS = new Set([
  'cultural',
  'adventure',
  'relaxation',
  'food',
  'urban',
  'nature',
  'beach',
  'luxury',
  'budget',
  'family',
  'romantic',
  'solo',
  'road-trip',
  'safari',
]);

/**
 * Curated topic/collection tags shown in the template browser filter panel.
 * Stored tag values must match template `tags` in Firestore exactly (kebab-case).
 */
export type TemplateCollection = {
  tag: string;
  label: string;
  eyebrow: string;
  description: string;
  ctaLabel: string;
};

export const TEMPLATE_TOPIC_FILTERS: ReadonlyArray<TemplateCollection> = [
  {
    tag: 'world-cup-2026',
    label: 'World Cup 2026',
    eyebrow: 'Seasonal collection',
    description: 'Host-city itineraries for match weekends, food, and recovery days.',
    ctaLabel: 'Explore World Cup templates',
  },
  {
    tag: 'america-250',
    label: 'America 250',
    eyebrow: 'Featured collection',
    description: 'Washington, DC and East Coast plans for the 2026 Semiquincentennial.',
    ctaLabel: 'Explore America 250',
  },
  {
    tag: 'calgary-stampede',
    label: 'Calgary Stampede',
    eyebrow: 'Seasonal collection',
    description: 'Stampede days, Banff add-ons, and western Canada highlights.',
    ctaLabel: 'Explore Stampede trips',
  },
];

/**
 * Turn stored tag tokens (e.g. world-cup-2026) into short display labels for UI chips.
 */
export function formatTemplateTagLabel(tag: string): string {
  const t = String(tag || '').trim();
  if (!t) return '';
  const known = TEMPLATE_TOPIC_FILTERS.find((entry) => entry.tag.toLowerCase() === t.toLowerCase());
  if (known) return known.label;
  return t
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Tags used for collections/campaigns — not travel-style labels. */
export function isTemplateTopicTag(tag: string, travelStyleKeys?: Set<string>): boolean {
  const normalized = String(tag || '').trim().toLowerCase();
  if (!normalized) return false;
  if (travelStyleKeys?.has(normalized)) return false;
  return !TRAVEL_STYLE_TAG_KEYS.has(normalized);
}

/** Normalize comma-separated tag input from the builder (lowercase, kebab-case). */
export function normalizeTemplateTagInput(raw: string): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
